import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  MapPin, Ruler, Settings, Printer, Maximize2, Plus, Save, Trash2, X, Loader2, TrendingUp, Eye, EyeOff
} from "lucide-react";
import { filterByState } from "../../hooks/useGlobalFilter";
import { formatDisplayDate } from "../../utils/formatters";
import { getRingNumeric } from "../../utils/helpers";
import { ROUTE_SEGMENTS, PROJECT_DEADLINE } from "../../utils/constants";
import { distancePlanFor } from "../../utils/planConfig";
import { routeConfigFor, routeRowsFromBored, machineActualMeters, ROUTE_TOTAL, PROJECT_TOTAL_M, ROUTE_NAME, ROUTE_STATUS, pct, validateRouteConfig } from "../../utils/routeConfig";
import { chartColors, axisTick, tooltipStyle } from "../../ui-ux-pro-max/chartTheme";
import {
  ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, ReferenceLine
} from "recharts";
import { fitAndPrint } from "../../utils/printFit";
import { buildMutationEnvelope } from "../../offline/mutationEnvelope";

const RouteScheduleView = ({ segmentRecords = [], projectInfo, machine = "TBM1", machineProgress = null, routeProjectTotal = null, filterState = {}, readOnly = false, onMutate, syncMeta, routeConfigs = null, distPlanConfig: distPlanConfigProp = null }) => {
  const filteredSegments = useMemo(() => filterByState(segmentRecords, filterState), [segmentRecords, filterState]);

  // this machine's route, stations and deadline — TBM2: IS4 → PS1, 4,726 m, no deadline yet
  const routeTotal = ROUTE_TOTAL[machine] || 0;
  const routeSegments = ROUTE_SEGMENTS[machine] || [];
  const deadline = PROJECT_DEADLINE[machine] || null;
  // distance axis: the route rounded up to 2,500 m — TBM1 keeps 0–10,000, TBM2 gets 0–5,000
  const yMax = Math.max(2500, Math.ceil(routeTotal / 2500) * 2500);
  const yTicks = [0, 1, 2, 3, 4].map((i) => (yMax * i) / 4);

  // ── Print State ──
  const [printingChartId, setPrintingChartId] = useState("all");

  const printGroupRef = useRef(null);

  const handlePrintSpecificChart = (chartId) => {
    setPrintingChartId(chartId);
    // zoom-to-fit หน้าเดียวแนวนอน — logic เดิมของหน้านี้ถูกสกัดไป utils/printFit.js แล้วใช้ร่วมทุกหน้า
    setTimeout(() => {
      fitAndPrint(printGroupRef.current, { orientation: "landscape", onePage: true });
      setPrintingChartId("all");
    }, 350);
  };

  const getPrintClass = (id) => {
    return printingChartId === "all" ? "" : (printingChartId === id ? "print-target" : "print:hidden");
  };

  // ── Distance Filter State ──
  const [distFilterMode, setDistFilterMode] = useState("all");
  const [distRangeStart, setDistRangeStart] = useState("");
  const [distRangeEnd, setDistRangeEnd] = useState("");
  const [expandedChart, setExpandedChart] = useState(null);

  // ── F3: Route distance table (project-wide TBM1+TBM2+รวม, config แก้ได้) ──
  // Both machines' configs come down from the snapshot; this table shows them side by side.
  const routeCfgFromProps = () => ({
    TBM1: routeConfigFor("TBM1", routeConfigs && routeConfigs.TBM1),
    TBM2: routeConfigFor("TBM2", routeConfigs && routeConfigs.TBM2),
  });
  const [routeCfg, setRouteCfg] = useState(routeCfgFromProps);
  const [routeEditing, setRouteEditing] = useState(false);
  // ซ่อนข้อความหมายเหตุเป็นค่าเริ่มต้น — ตารางแคบลง กราฟข้างๆ จึงกว้างพอให้ตัวเลขบนจุดไม่ทับกัน
  const [showRemark, setShowRemark] = useState(false);
  const [routeDraft, setRouteDraft] = useState(null);
  const [routeSaving, setRouteSaving] = useState(false);
  const [routeErr, setRouteErr] = useState([]);
  // A later render brings the server's answer; the cached snapshot was the first one. Never while
  // the table is being edited — a refresh mid-edit would discard the draft underneath it.
  useEffect(() => {
    if (!routeEditing) setRouteCfg(routeCfgFromProps());
    // eslint-disable-next-line
  }, [routeConfigs]);

  const boredByMachine = useMemo(() => ({
    TBM1: machine === "TBM1" ? machineActualMeters(segmentRecords) : (machineProgress && machineProgress.TBM1 ? machineProgress.TBM1.dist : 0),
    TBM2: machine === "TBM2" ? machineActualMeters(segmentRecords) : (machineProgress && machineProgress.TBM2 ? machineProgress.TBM2.dist : 0),
  }), [machine, segmentRecords, machineProgress]);

  const projTotal = routeProjectTotal || PROJECT_TOTAL_M;
  const routeTableSections = useMemo(() => ([
    { key: "TBM1", name: ROUTE_NAME.TBM1, total: ROUTE_TOTAL.TBM1, bored: boredByMachine.TBM1, rows: routeRowsFromBored("TBM1", routeCfg.TBM1, boredByMachine.TBM1) },
    { key: "TBM2", name: ROUTE_NAME.TBM2, total: ROUTE_TOTAL.TBM2, bored: boredByMachine.TBM2, rows: routeRowsFromBored("TBM2", routeCfg.TBM2, boredByMachine.TBM2) },
  ]), [routeCfg, boredByMachine]);

  const fmtM = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const routeStatusClass = (s) => s === ROUTE_STATUS.DONE ? "bg-sgreen-med/10 text-sgreen-dark border-sgreen-med/30" : s === ROUTE_STATUS.CURRENT ? "bg-code-b/10 text-code-b border-code-b/30" : "bg-surface-alt text-ink-3 border-line";

  const openRouteEditor = () => { setRouteDraft(JSON.parse(JSON.stringify(routeCfg[machine].legs || []))); setRouteErr([]); setRouteEditing(true); };
  const routeDraftChange = (i, field, val) => setRouteDraft((prev) => prev.map((l, idx) => idx === i ? { ...l, [field]: field === "plannedDistance" || field === "level" ? val : val } : l));
  const routeDraftAdd = () => setRouteDraft((prev) => [...prev, { order: "", level: 2, name: "", plannedDistance: 0, remark: "" }]);
  const routeDraftDel = (i) => setRouteDraft((prev) => prev.filter((_, idx) => idx !== i));
  const saveRouteEditor = async () => {
    const cfg = { legs: routeDraft.map((l) => ({ ...l, plannedDistance: parseFloat(l.plannedDistance) || 0, level: parseInt(l.level, 10) || 2 })) };
    const errs = validateRouteConfig(cfg);
    if (errs.length) { setRouteErr(errs); return; }
    setRouteSaving(true);
    setRouteCfg((prev) => ({ ...prev, [machine]: cfg }));
    // `routeProjectTotal` rides along: GAS stores it in its own PlanConfig row and the snapshot
    // merge already carries it across for a routeConfig edit
    try {
      if (onMutate) {
        await onMutate(buildMutationEnvelope({
          entityType: "routeConfig", operation: "update", machine, recordId: machine,
          payload: { routeConfig: cfg, routeProjectTotal: projTotal }, syncMeta,
        }));
      }
      // `setRouteCfg` above already changed the screen and nothing puts it back, so say the screen is
      // ahead of the sheet rather than naming a queue this no longer posts to.
    } catch (e) { alert("บันทึกเส้นทางไม่สำเร็จ — ยังไม่ขึ้นเซิร์ฟเวอร์: " + e.message + "\nหน้าจอยังแสดงค่าที่แก้ไว้ — โหลดแอปใหม่แล้วทำรายการนี้อีกครั้ง"); }
    setRouteSaving(false); setRouteEditing(false);
  };

  // ── Distance Plan Config ──
  const [showDistPlanModal, setShowDistPlanModal] = useState(false);
  const [distPlanConfig, setDistPlanConfig] = useState(() => distancePlanFor(distPlanConfigProp));
  // The prop is already scoped to the machine on screen — switching heads swaps the snapshot, which
  // is what used to be a re-read here. Not while the modal is open.
  useEffect(() => {
    if (!showDistPlanModal) setDistPlanConfig(distancePlanFor(distPlanConfigProp));
    // eslint-disable-next-line
  }, [distPlanConfigProp, machine]);

  // ── Distance Plan settings handlers ──
  const [isSavingDistPlan, setIsSavingDistPlan] = useState(false);
  const handleSaveDistPlanSettings = async () => {
    if (readOnly) return;
    setIsSavingDistPlan(true);
    try {
      // Every machine, not only TBM1. The sync path keys on `distPlanConfig_<machine>` and getData
      // reads it back the same way, so the "per-machine GAS = future" this guarded against arrived
      // with Task 6 — TBM2's distance plan had been local-only for no reason, which is the sole
      // durable source this task exists to end.
      if (onMutate) {
        await onMutate(buildMutationEnvelope({
          entityType: "distPlanConfig", operation: "update", machine, recordId: machine,
          payload: { distPlanConfig }, syncMeta,
        }));
      }
      setShowDistPlanModal(false);
    } catch (e) {
      console.error("Failed to save distance plan config", e);
      alert("บันทึกไม่สำเร็จ — ข้อมูลนี้ยังไม่ถูกเก็บไว้ที่ใด: " + (e && e.message ? e.message : e));
    } finally {
      setIsSavingDistPlan(false);
    }
  };
  const addDistPlanRange = () => setDistPlanConfig({ ...distPlanConfig, ranges: [...(distPlanConfig.ranges || []), { startMonth: "", endMonth: "", mode: "rings", ringsPerDay: 0, avgLength: 1.2, distancePerMonth: 0 }] });
  const removeDistPlanRange = (index) => { const r = [...(distPlanConfig.ranges || [])]; r.splice(index, 1); setDistPlanConfig({ ...distPlanConfig, ranges: r }); };

  // ══════════════════════════════════════════════
  // HELPER: Smart deduplicate — เลือก Completed ก่อน, ถ้าไม่มีใช้แถวสุดท้าย
  // ══════════════════════════════════════════════
  const deduplicateRecords = (records) => {
    const map = new Map();
    records.forEach(r => {
      const key = r.ringNo;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, r);
      } else {
        if (existing.status === "In Progress" && r.status !== "In Progress") {
          map.set(key, r);
        } else if (existing.status === r.status) {
          map.set(key, r);
        }
      }
    });
    return Array.from(map.values());
  };

  // ══════════════════════════════════════════════
  // SECTION: Distance Chart Data (รายเดือน)
  // ══════════════════════════════════════════════

  const distanceChartData = useMemo(() => {
    const allDeduped = deduplicateRecords(filteredSegments);

    // ใช้เฉพาะ Ring ถาวร (ให้สอดคล้องกับ Total Distance) ไม่ตัด In Progress ออกแล้ว
    const completedPermRings = allDeduped.filter(r => r.installType !== "Temporary");

    completedPermRings.sort((a, b) => {
      const prefA = String(a.ringNo).replace(/\d/g, '');
      const prefB = String(b.ringNo).replace(/\d/g, '');
      if (prefA !== prefB) return prefA.localeCompare(prefB);
      return getRingNumeric(a.ringNo) - getRingNumeric(b.ringNo);
    });

    const monthsMap = new Map();
    const carryOverMonth = "2025-11";

    completedPermRings.forEach(rec => {
      const dDate = formatDisplayDate(rec.date);
      if (!dDate) return;
      let monthKey = dDate.slice(0, 7);

      // ถ้ายอดเกิดก่อน พ.ย. 68 (2025-11) ให้ปัดไปรวมใน พ.ย. 68 (ยกยอดตามความต้องการ)
      if (monthKey < carryOverMonth) {
        monthKey = carryOverMonth;
      }

      if (!monthsMap.has(monthKey)) monthsMap.set(monthKey, { month: monthKey, distance: 0, rings: 0, hasActual: true });
      const d = monthsMap.get(monthKey);
      d.distance += parseFloat(rec.length || 0);
      d.rings++;
    });

    // เริ่มสร้าง sequence เดือนตั้งแต่เดือนแรกที่มีข้อมูล
    let minMonth = "2024-11";
    if (monthsMap.size > 0) {
      minMonth = Array.from(monthsMap.keys()).sort()[0];
    }

    // หาเดือนปัจจุบัน (เวลาไทย)
    const now = new Date();
    const nowTH = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Bangkok" }));
    const currentMonth = `${nowTH.getFullYear()}-${String(nowTH.getMonth() + 1).padStart(2, '0')}`;

    // เดือนสุดท้ายของกราฟ = เดือนกำหนดเสร็จของเครื่อง (TBM1 มิ.ย. 71) · ยังไม่มีกำหนด (TBM2) ⇒ null
    const projectEndMonth = deadline ? deadline.slice(0, 7) : null;

    let currentActualAcc = 0;
    let currentPlanAcc = 0;
    let currentMonthStr = minMonth;
    const result = [];

    // สร้างข้อมูลจนถึงเดือนกำหนดเสร็จ
    let loopCount = 0;
    while ((projectEndMonth == null || currentMonthStr <= projectEndMonth) && loopCount < 100) {
      let mData = monthsMap.get(currentMonthStr);
      let distThisMonth = mData ? mData.distance : 0;

      // Actual: นับเฉพาะเดือนที่มีข้อมูลจริงหรือเดือนที่ผ่านมาแล้ว
      if (currentMonthStr <= currentMonth) {
        currentActualAcc += distThisMonth;
      }

      // คำนวณ Plan สำหรับเดือนนี้
      let monthPlan = 0;
      if (distPlanConfig.ranges && distPlanConfig.ranges.length > 0) {
        for (const range of distPlanConfig.ranges) {
          if ((!range.startMonth || currentMonthStr >= range.startMonth) && (!range.endMonth || currentMonthStr <= range.endMonth)) {
            if (range.mode === "distance") {
              monthPlan = parseFloat(range.distancePerMonth) || 0;
            } else {
              const ringsPerDay = parseFloat(range.ringsPerDay) || 0;
              const avgLen = parseFloat(range.avgLength) || 1.2;
              monthPlan = ringsPerDay * avgLen * 30;
            }
            break;
          }
        }
      }
      currentPlanAcc += monthPlan;
      if (currentPlanAcc > routeTotal) currentPlanAcc = routeTotal;

      const [y, mo] = currentMonthStr.split("-");
      const thMonths = ["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."];
      const buddhistYear = (parseInt(y) + 543).toString().slice(-2);
      const displayMonth = `${thMonths[parseInt(mo) - 1]} ${buddhistYear}`;

      // Actual: เดือนอนาคตไม่แสดงจุด
      const isActualAvailable = currentMonthStr <= currentMonth;

      result.push({
        month: currentMonthStr,
        displayMonth,
        actualAcc: isActualAvailable ? (currentActualAcc > 0 ? Math.round(currentActualAcc * 10) / 10 : (mData ? 0 : 0)) : null,
        planAcc: Math.round(currentPlanAcc * 10) / 10,
        hasActual: !!mData,
        isFuture: currentMonthStr > currentMonth
      });

      // หยุดวาดเมื่อแผนถึงปลายทาง (ระยะรวมของเครื่อง) แล้ว และเลยเดือนปัจจุบัน
      // → ไม่ลากเส้นแบนต่อไปจนสุด deadline (จบกราฟที่เดือนขุดเสร็จจริง)
      if (currentPlanAcc >= routeTotal - 0.5 && currentMonthStr >= currentMonth) break;
      // ไม่มีกำหนดเสร็จ ⇒ ไม่มีปลายกราฟให้ยึด: จบที่เดือนปัจจุบัน เว้นแต่แผนยังเดินต่อ
      // ponytail: แผนที่เว้นเดือน (0) หลังเดือนปัจจุบันจะจบกราฟตรงนั้น — ใส่กำหนดเสร็จให้เครื่องเมื่อมี
      if (projectEndMonth == null && currentMonthStr >= currentMonth && monthPlan === 0) break;

      // Next month
      let ny = parseInt(y);
      let nm = parseInt(mo) + 1;
      if (nm > 12) { nm = 1; ny++; }
      currentMonthStr = `${ny}-${String(nm).padStart(2, '0')}`;
      loopCount++;
    }

    return result;
  }, [segmentRecords, filteredSegments, distPlanConfig, routeTotal, deadline]);

  // ระยะสะสมรวม (ทั้งหมด ไม่ filter) สำหรับ TBM position + summary
  const totalActualDistance = useMemo(() => {
    // หาค่า actualAcc ล่าสุดที่ไม่ใช่ null
    for (let i = distanceChartData.length - 1; i >= 0; i--) {
      if (distanceChartData[i].actualAcc !== null && distanceChartData[i].actualAcc > 0) {
        return distanceChartData[i].actualAcc;
      }
    }
    return 0;
  }, [distanceChartData]);

  // Filter ตามช่วงเดือน
  const filteredDistanceChartData = useMemo(() => {
    if (distFilterMode === "all" || !distRangeStart || !distRangeEnd) return distanceChartData;
    return distanceChartData.filter(d => d.month >= distRangeStart && d.month <= distRangeEnd);
  }, [distanceChartData, distFilterMode, distRangeStart, distRangeEnd]);

  const totalPlanDistance = useMemo(() => {
    // ใช้ planAcc ณ เดือนปัจจุบัน (ไม่ใช่เดือนสุดท้ายของโครงการ)
    const now = new Date();
    const nowTH = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Bangkok" }));
    const currentMonth = `${nowTH.getFullYear()}-${String(nowTH.getMonth() + 1).padStart(2, '0')}`;

    const currentMonthData = distanceChartData.find(d => d.month === currentMonth);
    if (currentMonthData) return currentMonthData.planAcc;

    // fallback: หาเดือนที่ใกล้เคียงที่สุดที่ไม่เกินเดือนปัจจุบัน
    for (let i = distanceChartData.length - 1; i >= 0; i--) {
      if (distanceChartData[i].month <= currentMonth) {
        return distanceChartData[i].planAcc;
      }
    }
    return 0;
  }, [distanceChartData]);

  // Delay warning
  const distanceDelay = useMemo(() => {
    if (totalPlanDistance <= 0) return null;
    const diff = totalPlanDistance - totalActualDistance;
    if (diff <= 0) return null;
    const ratePerMonth = 300;
    const monthsDelay = diff / ratePerMonth;
    return { diff: Math.round(diff * 100) / 100, months: Math.round(monthsDelay * 100) / 100, rate: ratePerMonth };
  }, [totalActualDistance, totalPlanDistance]);

  // คำนวณตำแหน่ง X (%) ของสถานีบน Route Progress โดยใช้ระยะจริง
  const routeStationPlacements = useMemo(() => {
    return routeSegments.map(seg => {
      const xPercent = routeTotal > 0 ? (seg.distance / routeTotal) * 100 : 0;
      return { ...seg, xPercent: Math.min(100, Math.max(0, xPercent)) };
    });
  }, [routeSegments, routeTotal]);

  const forecast = useMemo(() => {
    const data = distanceChartData;
    if (!data || data.length === 0 || totalActualDistance <= 0) return null;
    const firstIdx = data.findIndex((d) => d.actualAcc !== null && d.actualAcc > 0);
    let lastIdx = -1;
    for (let i = data.length - 1; i >= 0; i--) { if (data[i].actualAcc !== null && data[i].actualAcc > 0) { lastIdx = i; break; } }
    if (firstIdx < 0 || lastIdx < 0) return null;
    const elapsedMonths = Math.max(1, lastIdx - firstIdx + 1);
    const currentRate = totalActualDistance / elapsedMonths; // m/month
    const remaining = Math.max(0, routeTotal - totalActualDistance);
    const monthsToFinish = currentRate > 0 ? remaining / currentRate : null;
    const [cy, cm] = data[lastIdx].month.split("-").map(Number);
    const curIdx = cy * 12 + (cm - 1);
    const thMonths = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
    const fmtThai = (idx) => `${thMonths[((idx % 12) + 12) % 12]} ${String(Math.floor(idx / 12) + 543).slice(-2)}`;
    const finishIdx = monthsToFinish != null ? curIdx + Math.ceil(monthsToFinish) : null;
    const forecastLabel = finishIdx != null ? fmtThai(finishIdx) : "—";
    // เดือนกำหนดเสร็จของเครื่อง (TBM1 มิ.ย. 2028) · ไม่มีกำหนด ⇒ ไม่มีเรทที่ต้องเร่ง/ไม่ตัดสินว่าทันไหม
    const deadlineIdx = deadline ? Number(deadline.slice(0, 4)) * 12 + Number(deadline.slice(5, 7)) - 1 : null;
    const monthsToDeadline = deadlineIdx != null ? Math.max(0, deadlineIdx - curIdx) : 0;
    const requiredRate = deadlineIdx == null ? null : monthsToDeadline > 0 ? remaining / monthsToDeadline : 0;
    const onTime = deadlineIdx != null && finishIdx != null && finishIdx <= deadlineIdx;
    const deadlineLabel = deadlineIdx != null ? fmtThai(deadlineIdx) : null;
    return { currentRate, remaining, forecastLabel, requiredRate, onTime, deadlineLabel };
  }, [distanceChartData, totalActualDistance, routeTotal, deadline]);

  return (
    <div className="max-w-full mx-auto pb-24 animate-fade-in space-y-6 print:pb-0">

      {/* Route config editor */}
      {routeEditing && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-navy-dark/60 backdrop-blur-sm animate-fade-in no-print">
          <div className="bg-surface rounded-modal w-full max-w-3xl shadow-modal overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-navy px-6 py-4 text-white flex justify-between items-center shrink-0">
              <h3 className="font-semibold text-lg flex items-center gap-2"><Settings size={18} /> แก้ไขเส้นทาง — {machine}</h3>
              <button onClick={() => setRouteEditing(false)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-2">
              {routeErr.length > 0 && <div className="bg-code-d/10 border border-code-d/30 rounded-input p-3 text-xs text-code-d font-semibold space-y-1">{routeErr.map((e, i) => <div key={i}>• {e}</div>)}</div>}
              <div className="grid grid-cols-[70px_56px_1fr_100px_1fr_32px] gap-2 text-[10px] font-semibold text-ink-3 uppercase px-1">
                <span>ลำดับ</span><span>ชั้น</span><span>ชื่อช่วง</span><span>ระยะ (ม.)</span><span>หมายเหตุ</span><span></span>
              </div>
              {(routeDraft || []).map((l, i) => (
                <div key={i} className="grid grid-cols-[70px_56px_1fr_100px_1fr_32px] gap-2 items-center">
                  <input value={l.order} onChange={(e) => routeDraftChange(i, "order", e.target.value)} className="border border-line rounded-input px-2 py-1.5 text-xs font-mono bg-surface-alt outline-none focus:border-navy" placeholder="1.1" />
                  <input type="number" value={l.level} onChange={(e) => routeDraftChange(i, "level", e.target.value)} className="border border-line rounded-input px-2 py-1.5 text-xs font-mono bg-surface-alt outline-none focus:border-navy text-center" />
                  <input value={l.name} onChange={(e) => routeDraftChange(i, "name", e.target.value)} className="border border-line rounded-input px-2 py-1.5 text-xs bg-surface-alt outline-none focus:border-navy" placeholder="ชื่อช่วง" />
                  <input type="number" value={l.plannedDistance} onChange={(e) => routeDraftChange(i, "plannedDistance", e.target.value)} className="border border-line rounded-input px-2 py-1.5 text-xs font-mono bg-surface-alt outline-none focus:border-navy text-right" />
                  <input value={l.remark || ""} onChange={(e) => routeDraftChange(i, "remark", e.target.value)} className="border border-line rounded-input px-2 py-1.5 text-xs bg-surface-alt outline-none focus:border-navy" placeholder="หมายเหตุ" />
                  <button onClick={() => routeDraftDel(i)} className="p-1.5 text-code-d hover:bg-code-d/10 rounded-input" title="ลบแถว"><Trash2 size={14} /></button>
                </div>
              ))}
              <button onClick={routeDraftAdd} className="mt-2 px-3 py-1.5 text-xs font-semibold text-navy bg-cyan-tint rounded-input border border-line flex items-center gap-1"><Plus size={14} /> เพิ่มแถว</button>
            </div>
            <div className="px-5 py-3 border-t border-line flex justify-end gap-2 shrink-0">
              <button onClick={() => setRouteEditing(false)} className="px-4 py-2 bg-surface-alt text-ink-2 rounded-input font-semibold text-sm hover:bg-line">ยกเลิก</button>
              <button onClick={saveRouteEditor} disabled={routeSaving} className="px-4 py-2 bg-navy hover:bg-navy-dark text-white rounded-input font-semibold text-sm flex items-center gap-1">{routeSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} บันทึก</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          @page { size: landscape; margin: 10mm; }
          body { background: white !important; }
          .print-target { page-break-inside: avoid !important; }
          /* ตัด padding ท้าย container — กันความสูงเกินไปเปิดหน้า 2 เปล่า */
          main > div { padding: 0 !important; }
        }
      `}</style>

      {/* จอ ≥1700px: ตาราง+คาดการณ์ไปอยู่คอลัมน์ขวาข้างการ์ดกราฟ — แคบกว่านั้นไหลลงใต้กราฟตามเดิม
          580px ตายตัวทั้งสองสถานะของปุ่มหมายเหตุ: ถ้าคอลัมน์หดตอนซ่อน กราฟจะโดนบังคับ resize แล้ว
          recharts ค้าง layout เดิมจนเลขจุดสุดท้ายโดนตัด (แก้ด้วย key=remount ได้ แต่กราฟจะ animate ใหม่ทุกคลิก)
          minmax(...,896px) ก็ใช้ไม่ได้ — ตารางจะดูดไปเต็ม max-content แล้วบีบกราฟเหลือ ~720px
          print:block — ตอนพิมพ์ต้องคืนเป็น block ไม่งั้น grid ยังจองคอลัมน์ขวาที่ถูก print:hidden ไว้ */}
      <div className="grid grid-cols-1 gap-6 print:block min-[1700px]:grid-cols-[minmax(0,1fr)_580px]">

      {/* ═══ SECTION 3.5: แผนผังสถานะเส้นทางและตำแหน่ง TBM1 ปัจจุบัน ═══ */}
      {/* print group: zoom-to-fit หน้าเดียวใน handlePrintSpecificChart (การ์ดคาดการณ์อยู่นอก group + print:hidden) */}
      <div ref={printGroupRef} className={`space-y-6 ${getPrintClass('distance')}`}>
      <div className="bg-surface p-4 sm:p-6 rounded-card shadow-card border border-line flex flex-col gap-2 overflow-x-auto">
        {/* Header แถวที่ 1 */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-navy-dark tracking-tight">
              แผนผังสถานะเส้นทางและตำแหน่ง {projectInfo?.tbmNo || "TBM"} ปัจจุบัน
            </h2>
            <div className="text-xs sm:text-sm text-ink-2 mt-2 font-medium tracking-tight space-y-0.5">
              {machine !== "TBM1" ? <p>{ROUTE_NAME[machine]}</p> : <>
              <p><span className="font-semibold text-ink">เฟส 1 (Main Bore):</span> IS4-1 → IS2 → IS1 TBM เจาะต่อเนื่อง (เฟสปัจจุบัน)</p>
              <p><span className="font-semibold text-ink">เฟส 2 (Extension):</span> IS3 → เจาะเข้าอุโมงค์หลัก — TBM เจาะต่อไป ส่วนเชื่อม IS3 รอก่อสร้างในภายหลัง</p>
              </>}
            </div>
          </div>
          <div className="text-right mt-4 md:mt-0 flex items-center gap-3">
            {!readOnly && (
              <button onClick={() => handlePrintSpecificChart('distance')} className="p-2 text-ink-3 hover:text-navy bg-surface hover:bg-cyan-tint rounded-input transition-colors border border-line shadow-card print:hidden" title="Print Chart"><Printer size={18} /></button>
            )}
            <button onClick={() => setExpandedChart('distance')} className="p-2 text-ink-3 hover:text-navy bg-surface hover:bg-cyan-tint rounded-input transition-colors border border-line shadow-card print:hidden" title="Expand Chart"><Maximize2 size={18} /></button>
            <span className="bg-navy-dark text-white text-xs font-semibold px-4 py-2 rounded-input shadow-card">
              อัปเดตล่าสุด: {formatDisplayDate(new Date())}
            </span>
          </div>
        </div>

        {/* Header แถวที่ 2: Legends และ Delay Warning */}
        <div className="flex flex-col lg:flex-row justify-between items-center bg-surface-alt p-3 rounded-input border border-line">
          <div className="flex flex-wrap gap-6 items-center w-full lg:w-auto justify-center">
            <div className="flex items-center gap-2">
              <span className="w-8 h-1 rounded-full" style={{ backgroundColor: chartColors.delay }}></span>
              <span className="text-sm font-semibold text-ink">Actual Acc. (ผลงานจริงสะสม)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-0.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.planned }}></span><span className="w-5 h-1 rounded-full" style={{ backgroundColor: chartColors.planned }}></span></span>
              <span className="text-sm font-semibold text-ink">Plan Acc. (แผนงานสะสม)</span>
            </div>
            {!readOnly && (
              <button onClick={() => setShowDistPlanModal(true)} className="p-1.5 text-ink-3 hover:text-navy hover:bg-cyan-tint rounded-input transition-colors border border-transparent shadow-card print:hidden" title="Distance Plan Settings"><Settings size={18} /></button>
            )}
          </div>

          {/* Project Delay Warning Box */}
          {distanceDelay && (
            <div className="flex items-center gap-3 bg-code-d/10 border border-code-d/30 px-4 py-2 rounded-input shadow-card mt-4 lg:mt-0 lg:ml-auto">
              <div className="w-8 h-8 rounded-full bg-code-d/20 flex justify-center items-center overflow-hidden border border-code-d/30">
                <span className="text-code-d font-semibold text-lg">!</span>
              </div>
              <div className="text-left">
                <p className="text-[10px] font-semibold text-code-d mb-0.5 tracking-widest uppercase">Project Delay Warning</p>
                <p className="text-xs font-semibold text-ink">
                  ล่าช้ากว่าแผน: <span className="font-mono" style={{ color: chartColors.delay }}>{distanceDelay.diff.toLocaleString()} ม.</span>
                  <span className="text-[10px] text-ink-2 font-medium ml-1">({distanceDelay.months} เดือน)</span>
                  <span className="text-[10px] text-code-d font-medium ml-1">(คิดที่ {distanceDelay.rate} ม./เดือน)</span>
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="h-[380px] sm:h-[460px] w-full relative z-10">
          <div className="w-full h-full pl-2 pr-4 sm:pr-8">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={filteredDistanceChartData} margin={{ top: 35, right: 30, left: 10, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke={chartColors.grid} />
                <XAxis
                  dataKey="displayMonth"
                  tick={axisTick}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tickMargin={15}
                  axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
                  interval={0}
                  label={{ value: "เดือน/ปี", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: chartColors.axisLabel, fontWeight: "bold" } }}
                />
                <YAxis
                  domain={[0, yMax]}
                  ticks={yTicks}
                  tick={axisTick}
                  axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
                  tickLine={{ stroke: chartColors.axis }}
                  label={{ value: "ระยะทาง (เมตร)", angle: 0, position: "top", offset: 20, style: { fontSize: 11, fill: chartColors.axisLabel, fontWeight: "bold" } }}
                  width={60}
                  tickFormatter={val => val.toLocaleString()}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(val, name) => {
                    if (val === null || val === undefined) return ['-', name === "planAcc" ? "แผนงานสะสม" : "ผลงานจริงสะสม"];
                    return [`${Number(val).toLocaleString()} m`, name === "planAcc" ? "แผนงานสะสม" : "ผลงานจริงสะสม"];
                  }}
                />

                {/* เส้น Plan — จบที่เดือนขุดเสร็จ (กำหนดเสร็จของเครื่อง) */}
                <Line
                  type="monotone"
                  dataKey="planAcc"
                  stroke={chartColors.planned}
                  strokeWidth={3}
                  connectNulls={false}
                  activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                  isAnimationActive={printingChartId === "all"}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (cx === undefined || cy === undefined) return null;
                    return (
                      <g key={`dot-plan-${payload.month}`}>
                        <circle cx={cx} cy={cy} r={4} fill={chartColors.planned} />
                        {payload.planAcc > 0 && <text x={cx} y={cy - 12} fill={chartColors.planned} fontSize={9} fontWeight="bold" textAnchor="middle">{payload.planAcc.toLocaleString(undefined, {maximumFractionDigits:0})}</text>}
                      </g>
                    );
                  }}
                />

                {/* เส้น Actual — หยุดที่เดือนปัจจุบัน */}
                <Line
                  type="monotone"
                  dataKey="actualAcc"
                  stroke={chartColors.delay}
                  strokeWidth={3}
                  connectNulls={true}
                  activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                  isAnimationActive={printingChartId === "all"}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (payload.actualAcc === null || payload.actualAcc === undefined || cx === undefined || cy === undefined) return null;
                    return (
                      <g key={`dot-act-${payload.month}`}>
                        <rect x={cx - 4} y={cy - 4} width={8} height={8} fill={chartColors.delay} rx={1} />
                        {payload.actualAcc > 0 && <text x={cx} y={cy + 18} fill={chartColors.delay} fontSize={9} fontWeight="bold" textAnchor="middle">{payload.actualAcc.toLocaleString(undefined, {maximumFractionDigits:0})}</text>}
                      </g>
                    );
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ═══ ส่วนที่ 2: Progress Bar — เปรียบเทียบแผน vs ผลงาน ณ เดือนปัจจุบัน ═══ */}
        <div className="relative min-w-[700px] overflow-hidden bg-surface-alt border border-line rounded-input p-4 shadow-card">
          {/* หัวข้อ + ระยะทางรวม */}
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs font-semibold text-ink-2 uppercase tracking-wider">เปรียบเทียบ ณ เดือนปัจจุบัน</span>
            <span className="text-[11px] font-semibold text-ink-2">ระยะโครงการ: <span className="text-ink font-mono font-semibold">{routeTotal.toLocaleString()} ม.</span></span>
          </div>

          <div className="flex flex-col gap-3 relative z-20">
            {/* แผนงานสะสม ณ เดือนปัจจุบัน */}
            <div className="flex items-center h-[38px]">
              <span className="w-32 text-xs font-semibold text-ink shrink-0">แผนงานสะสม</span>
              <div className="flex-1 h-full rounded-input border border-line relative overflow-hidden bg-surface-page">
                {totalPlanDistance > 0 ? (
                  <>
                    <div
                      className="h-full bg-navy-dark rounded-input flex items-center justify-end px-3 relative z-10 shadow-card transition-all duration-700"
                      style={{ width: `${Math.max((totalPlanDistance / routeTotal) * 100, 5)}%` }}
                    >
                      <span className="text-white text-xs font-mono font-semibold drop-shadow-md whitespace-nowrap">{totalPlanDistance.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                    </div>
                    {/* ส่วนเหลือของโครงการ (สีจาง) */}
                    <div className="absolute top-0 right-2 h-full flex items-center z-0">
                      <span className="text-[9px] font-semibold text-ink-3">{routeTotal.toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-ink-2 text-xs font-semibold">{readOnly ? "ยังไม่ตั้งค่าแผน" : "ยังไม่ตั้งค่าแผน — กดปุ่ม ⚙️ เพื่อตั้งค่า"}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ผลงานจริง */}
            <div className="flex items-center h-[38px] relative">
              <span className="w-32 text-xs font-semibold text-ink shrink-0">ผลงานจริง</span>
              <div className="flex-1 h-full rounded-input border border-line relative overflow-hidden bg-surface-page">
                {(() => {
                  const planPercent = totalPlanDistance > 0 ? (totalPlanDistance / routeTotal) * 100 : 0;
                  const actualPercent = (totalActualDistance / routeTotal) * 100;
                  const gapWidth = planPercent - actualPercent;

                  return (
                    <>
                      {/* ส่วนที่ทำได้ (สีเขียว) */}
                      {totalActualDistance > 0 && (
                        <div
                          className="absolute top-0 left-0 h-full bg-sgreen-dark flex items-center justify-center z-10 shadow-card transition-all duration-700"
                          style={{ width: `${Math.max(actualPercent, 3)}%`, borderRight: totalPlanDistance > 0 ? '2px solid #0C2C65' : 'none' }}
                        >
                          <span className="text-white text-[10px] font-mono font-semibold whitespace-nowrap px-1 drop-shadow-md">
                            {totalActualDistance.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          </span>
                        </div>
                      )}
                      {/* ส่วนที่ยังขาดจากแผน (ลายขวาง) */}
                      {totalPlanDistance > 0 && gapWidth > 0 && (
                        <div
                          className="absolute top-0 h-full flex items-center justify-center z-5"
                          style={{
                            left: `${actualPercent}%`,
                            width: `${gapWidth}%`,
                            backgroundImage: "repeating-linear-gradient(45deg, #fecaca 0px, #fecaca 6px, #fff 6px, #fff 12px)"
                          }}
                        >
                          <span className="text-code-d text-[9px] font-mono font-semibold bg-white/90 px-1.5 py-0.5 rounded-badge shadow-card whitespace-nowrap border border-code-d/30 z-10 relative">
                            -{(totalPlanDistance - totalActualDistance).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                          </span>
                        </div>
                      )}
                      {/* ส่วนเหลือของโครงการ */}
                      <div className="absolute top-0 right-2 h-full flex items-center z-0">
                        <span className="text-[9px] font-semibold text-ink-3">{routeTotal.toLocaleString()}</span>
                      </div>
                      {/* ไม่มีผลงานเลย */}
                      {totalActualDistance === 0 && (
                        <div className="w-full h-full flex items-center justify-center z-20 relative">
                          <span className="text-ink-3 text-[10px] font-semibold">ยังไม่มีผลงาน</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ ส่วนที่ 3: Route Progress — แสดงเต็มเส้นทาง ═══ */}
        <div className="relative min-w-[700px] overflow-hidden bg-surface border border-line rounded-input p-4 shadow-card">
          <style>{`
            .track-pattern {
              background-image: repeating-linear-gradient(90deg, transparent, transparent 15px, #94a3b8 15px, #94a3b8 18px);
            }
            .track-pattern-light {
              background-image: repeating-linear-gradient(90deg, transparent, transparent 15px, #cbd5e1 15px, #cbd5e1 18px);
            }
            .track-pattern-vertical {
              background-image: repeating-linear-gradient(0deg, transparent, transparent 10px, #94a3b8 10px, #94a3b8 13px);
            }
            .track-pattern-light-vertical {
              background-image: repeating-linear-gradient(0deg, transparent, transparent 10px, #cbd5e1 10px, #cbd5e1 13px);
            }
          `}</style>

          <div className="flex justify-between items-center mb-2">
            <span className="text-ink-2 text-[11px] font-semibold tracking-[0.2em] uppercase">Route Progress</span>
          </div>

          <div className="relative mx-4 sm:mx-8" style={{ height: '95px' }}>
            {/* แถบราง: สถานีแรก → สถานีสุดท้ายของเครื่อง (ต่อเนื่อง สีปกติ) */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[18px] bg-surface-alt border-y-[3px] border-line track-pattern shadow-card z-0"></div>

            {/* พื้นที่ของผลงานจริง */}
            <div
              className="absolute top-1/2 -translate-y-1/2 left-0 h-[32px] rounded-l-full z-1 transition-all duration-1000"
              style={{ width: `${Math.min((totalActualDistance / routeTotal) * 100, 100)}%`, backgroundColor: `${chartColors.delay}33`, border: `1px solid ${chartColors.delay}66` }}
            ></div>

            {/* เส้นแบ่งเฟสแนวตั้งประที่ IS3 */}
            {(() => {
              const is3Seg = routeStationPlacements.find(s => s.id === "IS3");
              if (!is3Seg) return null;
              return (
                <div
                  className="absolute z-20"
                  style={{ left: `${is3Seg.xPercent}%`, top: 'calc(50% - 16px)', transform: 'translateX(-50%)', width: 2, height: 32, borderLeft: "2.5px dashed #f59e0b" }}
                />
              );
            })()}

            {/* อุโมงค์แยกส่วนแนวตั้งที่ IS3 (สีอ่อนกว่า) */}
            {(() => {
              const is3Seg = routeStationPlacements.find(s => s.id === "IS3");
              if (!is3Seg) return null;
              return (
                <div
                  className="absolute z-0"
                  style={{ left: `calc(${is3Seg.xPercent}%)`, top: 'calc(50% - 30px)', width: 2, height: '25px', borderLeft: '1.5px dashed #f59e0b' }}
                />
              );
            })()}

            {/* หมุดสถานี */}
            {routeStationPlacements.map((seg, i) => {
              if (i === 0) return null;
              const prevSeg = routeStationPlacements[i - 1];
              const isIS3 = seg.id === "IS3";
              return (
                <React.Fragment key={`route-${seg.id}`}>
                  {/* กรอบแสดงระยะระหว่างสถานี */}
                  <div
                    className="absolute -top-6 -translate-x-1/2 bg-surface border-2 border-navy rounded-full px-2 py-0.5 text-[9px] font-semibold font-mono text-ink shadow-card whitespace-nowrap z-10"
                    style={{ left: `${(prevSeg.xPercent + seg.xPercent) / 2}%` }}
                  >
                    {((seg.distance - routeSegments[i - 1].distance)).toLocaleString(undefined, { minimumFractionDigits: 3 })} m
                  </div>

                  {/* หมุดสถานี */}
                  <div
                    className="absolute flex flex-col items-center justify-center z-20"
                    style={isIS3 ? { left: `${seg.xPercent}%`, top: `calc(50% - 38px)`, transform: 'translateX(-50%) translateY(-50%)' } : { left: `${seg.xPercent}%`, top: `50%`, transform: 'translateX(-50%) translateY(-50%)' }}
                  >
                    {isIS3 ? (
                      <div className="relative flex flex-col items-center">
                        <span className="absolute whitespace-nowrap text-[9px] font-semibold text-code-b bg-code-b/10 border border-code-b/30 px-1.5 py-0.5 rounded-badge shadow-card tracking-wide" style={{ top: -24 }}>
                          D-wall
                        </span>
                        <div className="relative bg-navy-dark border-[1.5px] border-navy rounded-input p-1 shadow-card flex items-center justify-center z-10 w-11 h-11 pointer-events-none">
                          <div className="bg-surface w-full h-full flex items-center justify-center rounded-badge">
                            <div className="w-6 h-6 rounded-full text-white flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: chartColors.planned }}>
                              {seg.label.replace("IS", "")}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative w-9 h-9 rounded-full border-[4px] border-white shadow-hover text-white flex items-center justify-center text-[10px] font-semibold z-10 tracking-tighter" style={{ backgroundColor: chartColors.planned }}>
                        {seg.label.replace("IS", "")}
                      </div>
                    )}
                    <span className="absolute text-[10px] font-semibold text-ink whitespace-nowrap" style={{ top: isIS3 ? 66 : 44 }}>{seg.label}</span>
                  </div>
                </React.Fragment>
              );
            })}

            {/* หมุดสถานีแรก (ซ้ายสุด) */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 flex flex-col items-center justify-center z-20">
              <div className="relative w-9 h-9 rounded-full border-[4px] border-white shadow-hover text-white flex items-center justify-center text-[10px] font-semibold z-10 tracking-tighter" style={{ backgroundColor: chartColors.planned }}>
                {routeSegments[0].label.replace("IS", "")}
              </div>
              <span className="absolute top-11 text-[10px] font-semibold text-ink whitespace-nowrap">{routeSegments[0].label}</span>
            </div>

            {/* หมุดสถานีปัจจุบัน (TBM) */}
            <div
              className="absolute top-1/2 -translate-y-1/2 z-30 transition-all duration-[1500ms] ease-out"
              style={{ left: `${Math.min((totalActualDistance / routeTotal) * 100, 100)}%`, transform: 'translateX(-50%) translateY(-50%)' }}
            >
              <div className="relative drop-shadow-xl group cursor-pointer">
                <span className="absolute -inset-2 rounded-full opacity-40 animate-ping" style={{ backgroundColor: chartColors.delay }}></span>
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-semibold text-ink bg-surface/90 backdrop-blur-sm px-1.5 py-0.5 rounded-badge shadow-card border border-line z-50">
                  Progress : {((totalActualDistance / routeTotal) * 100).toFixed(2)} %
                </div>
                <div className="w-8 h-8 bg-navy border-[3px] border-white flex items-center justify-center rounded-badge rotate-90 relative z-10 shadow-hover overflow-hidden">
                  <div className="w-full h-[60%] bg-navy-dark absolute bottom-0 left-0"></div>
                  <div className="w-full h-[2px] bg-white opacity-50 absolute top-[40%] left-0"></div>
                  <span className="text-[8px] text-white font-semibold z-10 -rotate-90">{projectInfo?.tbmNo || "TBM"}</span>
                </div>
                <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-navy-dark text-white text-[10px] px-3 py-1.5 rounded-input font-semibold whitespace-nowrap shadow-modal opacity-0 group-hover:opacity-100 transition-opacity z-40 pointer-events-none">
                  {projectInfo?.tbmNo || "TBM"}: {totalActualDistance.toLocaleString()} m ({(totalActualDistance / routeTotal * 100).toFixed(1)}%)
                </div>
              </div>
            </div>
          </div>
          {/* spacer below route */}
          <div className="h-8"></div>
        </div>
      </div>
      </div>{/* /print group */}

      {/* คอลัมน์ขวา — ตาราง + คาดการณ์ ; flex-col เพื่อให้ตารางยืดเติมความสูงจนเท่าการ์ดกราฟ */}
      <div className="flex flex-col gap-6 print:block">

      {/* ═══ F3: ตารางระยะทางเส้นทาง (project-wide) — ข้างกราฟ ═══ */}
      <div className={`bg-surface rounded-card shadow-card border border-line overflow-hidden max-w-5xl flex flex-col flex-1 print:block ${getPrintClass('routetable')}`}>
        <div className="px-5 sm:px-6 py-4 border-b border-line flex flex-col sm:flex-row justify-between sm:items-center bg-surface-alt gap-2">
          <div>
            <h3 className="font-semibold text-ink text-base flex items-center gap-2"><Ruler size={18} className="text-navy" /> ผลงานสะสมงานอุโมงค์ ({machine})</h3>
            <p className="text-xs text-ink-3 font-semibold mt-0.5">
              {machineProgress && machineProgress[machine] ? `${machineProgress[machine].rings} Ring · ` : ""}
              {fmtM(boredByMachine[machine])} m · {pct(boredByMachine[machine], ROUTE_TOTAL[machine] || projTotal).toFixed(2)}%
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button onClick={() => setShowRemark((v) => !v)} className="px-3 py-1.5 text-xs font-semibold text-navy bg-surface hover:bg-cyan-tint rounded-input border border-line shadow-card flex items-center gap-1" title={showRemark ? "ซ่อนข้อความหมายเหตุ (กราฟกว้างขึ้น)" : "แสดงข้อความหมายเหตุ"}>
              {showRemark ? <EyeOff size={14} /> : <Eye size={14} />} หมายเหตุ
            </button>
            {!readOnly && <button onClick={() => handlePrintSpecificChart('routetable')} className="p-1.5 text-ink-3 hover:text-navy bg-surface hover:bg-cyan-tint rounded-input border border-line shadow-card" title="Print"><Printer size={16} /></button>}
            {!readOnly && <button onClick={openRouteEditor} className="px-3 py-1.5 text-xs font-semibold text-navy bg-surface hover:bg-cyan-tint rounded-input border border-line shadow-card flex items-center gap-1"><Settings size={14} /> แก้ไขเส้นทาง</button>}
          </div>
        </div>
        <div className="overflow-x-auto flex-1 print:flex-none">
          {/* h-full: กระจายแถวเติมความสูงที่ยืดมา แทนที่จะทิ้งช่องว่างไว้ใต้แถวสุดท้าย */}
          <table className="w-full h-full text-sm text-left whitespace-nowrap">
            <thead className="text-xs text-white uppercase bg-navy-dark">
              <tr><th className="px-4 py-3 w-16">ลำดับ</th><th className="px-4 py-3">เส้นทางชุดเจาะอุโมงค์</th><th className="px-4 py-3 text-right">ระยะทาง (ม.)</th><th className={`px-4 py-3 ${showRemark ? "w-2/5" : ""}`}>หมายเหตุ</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              <tr><td></td><td className="px-4 py-2 font-semibold text-ink">ระยะทางโครงการทั้งหมด</td><td className="px-4 py-2 text-right font-mono font-semibold">{fmtM(projTotal)}</td><td></td></tr>
              {routeTableSections.map((sec) => (
                <React.Fragment key={sec.key}>
                  <tr className="bg-surface-alt"><td className="px-4 py-2 font-mono font-semibold text-ink">{sec.key === "TBM1" ? "1" : "2"}</td><td className="px-4 py-2 font-semibold text-ink">{sec.name}</td><td className="px-4 py-2 text-right font-mono font-semibold">{fmtM(sec.total)}</td><td></td></tr>
                  {sec.rows.map((r) => (
                    <tr key={r.order} className="hover:bg-cyan-tint/40">
                      <td className="px-4 py-2 font-mono text-ink-3">{r.order}</td>
                      <td className="px-4 py-2 text-ink-2" style={{ paddingLeft: 16 + (Math.max(1, r.level) - 1) * 18 }}>{r.name}</td>
                      <td className="px-4 py-2 text-right font-mono text-ink">{fmtM(r.displayDistance)}</td>
                      <td className="px-4 py-2 whitespace-normal"><span className={`text-[11px] px-2 py-0.5 rounded-badge border font-semibold whitespace-nowrap inline-block ${routeStatusClass(r.status)}`}>{r.status}</span>{showRemark && r.remark ? <span className="text-[11px] text-ink-3 ml-2">{r.remark}</span> : null}</td>
                    </tr>
                  ))}
                  <tr className="bg-sgreen-med/10"><td></td><td className="px-4 py-2 font-semibold text-sgreen-dark">ผลงาน {sec.key} เจาะได้ระยะทางรวม</td><td className="px-4 py-2 text-right font-mono font-semibold text-sgreen-dark">{fmtM(sec.bored)}</td><td className="px-4 py-2 font-semibold text-sgreen-dark">{pct(sec.bored, sec.total).toFixed(2)}%</td></tr>
                </React.Fragment>
              ))}
              <tr className="bg-navy text-white"><td></td><td className="px-4 py-2 font-semibold">ผลงานรวม</td><td className="px-4 py-2 text-right font-mono font-semibold">{fmtM(boredByMachine.TBM1 + boredByMachine.TBM2)}</td><td className="px-4 py-2 font-semibold">{pct(boredByMachine.TBM1 + boredByMachine.TBM2, projTotal).toFixed(2)}%</td></tr>
            </tbody>
          </table>
        </div>
        <p className="px-5 py-2 text-[11px] text-ink-3 print:hidden">สถานะคำนวณอัตโนมัติจากระยะขุดจริง · leaf ที่กำลังทำแสดงระยะที่ทำได้จริง · ตัวเลข seed แก้ได้ในปุ่ม "แก้ไขเส้นทาง"</p>
      </div>

      {/* คาดการณ์ — ไม่พิมพ์ (user: "ไม่ต้องปริ้นส่วนนี้") */}
      {forecast && (
        <div className="bg-surface rounded-card p-5 shadow-card border border-line print:hidden">
          <h3 className="font-semibold text-ink text-base mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-navy" /> คาดการณ์ (Forecast)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs font-semibold text-ink-3 uppercase mb-1">คาดเสร็จ (rate ปัจจุบัน)</div>
              <div className={`text-xl font-semibold font-mono ${!forecast.deadlineLabel ? "text-navy" : forecast.onTime ? "text-sgreen-dark" : "text-code-d"}`}>{forecast.forecastLabel}</div>
              <div className="text-[11px] text-ink-2 mt-0.5">{!forecast.deadlineLabel ? "ยังไม่มีกำหนดเสร็จ" : forecast.onTime ? `✓ ทันกำหนด ${forecast.deadlineLabel}` : `⚠ ช้ากว่ากำหนด ${forecast.deadlineLabel}`}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-ink-3 uppercase mb-1">rate ปัจจุบัน</div>
              <div className="text-xl font-semibold font-mono text-navy">{forecast.currentRate.toFixed(0)} <span className="text-xs text-ink-3">ม./เดือน</span></div>
              <div className="text-[11px] text-ink-2 mt-0.5">เหลืออีก {forecast.remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })} ม.</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-ink-3 uppercase mb-1">ต้องเร่งเป็น</div>
              <div className="text-xl font-semibold font-mono text-code-c">{forecast.requiredRate != null ? <>{forecast.requiredRate.toFixed(0)} <span className="text-xs text-ink-3">ม./เดือน</span></> : "—"}</div>
              <div className="text-[11px] text-ink-2 mt-0.5">{forecast.deadlineLabel ? `เพื่อทันกำหนด ${forecast.deadlineLabel}` : "ยังไม่มีกำหนดเสร็จ"}</div>
            </div>
          </div>
        </div>
      )}

      </div>{/* /คอลัมน์ขวา */}
      </div>{/* /grid ตารางข้างกราฟ */}

      {/* ═══ Distance Plan Settings Modal ═══ */}
      {showDistPlanModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy-dark/60 backdrop-blur-sm animate-fade-in no-print">
          <div className="bg-surface rounded-modal w-full max-w-xl shadow-modal overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-navy px-6 py-4 text-white flex justify-between items-center shrink-0">
              <h3 className="font-semibold text-lg flex items-center gap-2"><Ruler size={20} /> ตั้งค่าแผนระยะขุดเจาะ (Distance Plan)</h3>
              <button onClick={() => setShowDistPlanModal(false)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"><X size={20} /></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-ink text-sm">ช่วงเวลาแผนระยะทาง</h4>
                  <button onClick={addDistPlanRange} className="text-navy hover:text-navy-dark bg-cyan-tint hover:bg-cyan-tint/80 px-2.5 py-1 rounded-input text-xs font-semibold flex items-center gap-1 transition-colors border border-line"><Plus size={14} /> เพิ่มช่วง</button>
                </div>
                <div className="space-y-4">
                  {(distPlanConfig.ranges || []).map((range, index) => (
                    <div key={index} className="bg-surface-alt p-4 rounded-input border border-line space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-semibold text-ink-3 block mb-1">เดือนเริ่ม</label>
                            <input type="month" value={range.startMonth} onChange={e => { const nr = [...(distPlanConfig.ranges || [])]; nr[index].startMonth = e.target.value; setDistPlanConfig({ ...distPlanConfig, ranges: nr }); }} className="w-full bg-surface border border-line rounded-input p-1.5 text-xs outline-none focus:border-navy text-ink" />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-ink-3 block mb-1">เดือนสิ้นสุด</label>
                            <input type="month" value={range.endMonth} onChange={e => { const nr = [...(distPlanConfig.ranges || [])]; nr[index].endMonth = e.target.value; setDistPlanConfig({ ...distPlanConfig, ranges: nr }); }} className="w-full bg-surface border border-line rounded-input p-1.5 text-xs outline-none focus:border-navy text-ink" />
                          </div>
                        </div>
                        <button onClick={() => removeDistPlanRange(index)} className="p-1.5 text-code-d hover:text-code-d hover:bg-code-d/10 rounded-input mt-4 transition-colors"><Trash2 size={16} /></button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select value={range.mode || "rings"} onChange={e => { const nr = [...(distPlanConfig.ranges || [])]; nr[index].mode = e.target.value; setDistPlanConfig({ ...distPlanConfig, ranges: nr }); }} className="bg-surface border border-line rounded-input p-1.5 text-xs outline-none focus:border-navy font-semibold text-ink">
                          <option value="rings">Rings/Day</option>
                          <option value="distance">Distance/Month</option>
                        </select>
                        {(range.mode || "rings") === "rings" ? (
                          <div className="flex items-center gap-2 flex-1">
                            <div>
                              <label className="text-[10px] font-semibold text-ink-3 block mb-1">Ring/วัน</label>
                              <input type="number" step="0.5" value={range.ringsPerDay} onChange={e => { const nr = [...(distPlanConfig.ranges || [])]; nr[index].ringsPerDay = Number(e.target.value); setDistPlanConfig({ ...distPlanConfig, ranges: nr }); }} className="w-20 bg-surface border border-line rounded-input p-1.5 text-xs outline-none focus:border-navy font-mono text-center font-semibold text-ink" />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-ink-3 block mb-1">ยาวเฉลี่ย (m)</label>
                              <input type="number" step="0.1" value={range.avgLength} onChange={e => { const nr = [...(distPlanConfig.ranges || [])]; nr[index].avgLength = Number(e.target.value); setDistPlanConfig({ ...distPlanConfig, ranges: nr }); }} className="w-20 bg-surface border border-line rounded-input p-1.5 text-xs outline-none focus:border-navy font-mono text-center font-semibold text-ink" />
                            </div>
                            <div className="text-[10px] text-ink-3 font-semibold mt-4">≈ {((parseFloat(range.ringsPerDay) || 0) * (parseFloat(range.avgLength) || 1.2) * 30).toFixed(0)} m/เดือน</div>
                          </div>
                        ) : (
                          <div>
                            <label className="text-[10px] font-semibold text-ink-3 block mb-1">ระยะ/เดือน (m)</label>
                            <input type="number" value={range.distancePerMonth} onChange={e => { const nr = [...(distPlanConfig.ranges || [])]; nr[index].distancePerMonth = Number(e.target.value); setDistPlanConfig({ ...distPlanConfig, ranges: nr }); }} className="w-28 bg-surface border border-line rounded-input p-1.5 text-xs outline-none focus:border-navy font-mono text-center font-semibold text-ink" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(distPlanConfig.ranges || []).length === 0 && <div className="text-center p-4 text-xs text-ink-3 bg-surface-alt rounded-input border border-dashed border-line">ไม่พบช่วงเวลาแผน (ใช้ Default 0 m/เดือน)</div>}
                </div>
                <p className="text-[10px] text-ink-2 mt-2">หมายเหตุ* : หากมีช่วงเวลาทับซ้อนกัน จะใช้ข้อมูลจากลำดับแรกที่ตรงกัน</p>
              </div>
            </div>
            <div className="p-4 bg-surface-alt border-t border-line flex justify-end gap-2 shrink-0">
              <button onClick={() => setShowDistPlanModal(false)} className="px-5 py-2.5 bg-surface text-ink-2 rounded-input text-sm font-semibold border border-line hover:bg-surface-alt shadow-card transition-colors" disabled={isSavingDistPlan}>ยกเลิก</button>
              <button onClick={handleSaveDistPlanSettings} disabled={isSavingDistPlan} className="px-5 py-2.5 bg-navy text-white rounded-input text-sm font-semibold shadow-hover hover:opacity-90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSavingDistPlan ? <><Loader2 size={16} className="animate-spin" /> กำลังบันทึก...</> : <><Save size={16} /> บันทึกการตั้งค่า</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Chart Expansion Modal (distance branch) ═══ */}
      {expandedChart === 'distance' && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-8 bg-navy-dark/80 backdrop-blur-sm animate-fade-in no-print">
          <div className="bg-surface rounded-modal w-full h-full max-w-[1400px] max-h-[90vh] shadow-modal overflow-hidden flex flex-col">
            <div className="bg-navy-dark px-6 py-4 text-white flex justify-between items-center shrink-0">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <MapPin size={20} /> รายงานความก้าวหน้างานขุดเจาะอุโมงค์ {projectInfo?.tbmNo || "TBM"}
              </h3>
              <button onClick={() => setExpandedChart(null)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"><X size={20} /></button>
            </div>
            <div className="p-4 sm:p-6 flex-1 overflow-hidden flex flex-col bg-surface-alt">
              <div className="flex-1 w-full h-full bg-surface rounded-input border border-line shadow-card p-4 sm:p-6">
                <div className="w-full h-full flex flex-col gap-4 overflow-y-auto pr-2 pb-4">
                  <div className="flex justify-center shrink-0">
                    <div className="flex flex-wrap gap-6 items-center bg-surface-alt px-6 py-3 rounded-input border border-line shadow-card">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-1 rounded-full" style={{ backgroundColor: chartColors.delay }}></span>
                        <span className="text-[11px] sm:text-sm font-semibold text-ink">Actual Acc. (ผลงานจริงสะสม)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-0.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.planned }}></span><span className="w-5 h-1 rounded-full" style={{ backgroundColor: chartColors.planned }}></span></span>
                        <span className="text-[11px] sm:text-sm font-semibold text-ink">Plan Acc. (แผนงานสะสม)</span>
                      </div>
                    </div>
                  </div>
                  {/* ═══ ส่วนที่ 1: กราฟเส้น (Line Chart) ═══ */}
                  <div className="flex-1 min-h-[400px] w-full relative z-10 shrink-0">
                    <div className="w-full h-full pl-2 pr-4 sm:pr-8">
                      <ResponsiveContainer>
                        <ComposedChart data={filteredDistanceChartData} margin={{ top: 35, right: 30, left: 10, bottom: 15 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke={chartColors.grid} />
                          <XAxis
                            dataKey="displayMonth"
                            tick={axisTick}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tickMargin={15}
                            axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
                            interval={0}
                            label={{ value: "เดือน/ปี", position: "insideBottomRight", offset: -5, style: { fontSize: 11, fill: chartColors.axisLabel, fontWeight: "bold" } }}
                          />
                          <YAxis
                            domain={[0, yMax]}
                            ticks={yTicks}
                            tick={axisTick}
                            axisLine={{ stroke: chartColors.axis, strokeWidth: 1 }}
                            tickLine={{ stroke: chartColors.axis }}
                            label={{ value: "ระยะทาง (เมตร)", angle: 0, position: "top", offset: 20, style: { fontSize: 11, fill: chartColors.axisLabel, fontWeight: "bold" } }}
                            width={60}
                            tickFormatter={val => val.toLocaleString()}
                          />
                          <Tooltip
                            {...tooltipStyle}
                            formatter={(val, name) => {
                              if (val === null || val === undefined) return ['-', name === "planAcc" ? "แผนงานสะสม" : "ผลงานจริงสะสม"];
                              return [`${Number(val).toLocaleString()} m`, name === "planAcc" ? "แผนงานสะสม" : "ผลงานจริงสะสม"];
                            }}
                          />
                          <Line type="monotone" dataKey="planAcc" stroke={chartColors.planned} strokeWidth={3} connectNulls={false} activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }} dot={(props) => { const { cx, cy, payload } = props; if (cx === undefined || cy === undefined) return null; return ( <g key={`dot-plan-${payload.month}`}> <circle cx={cx} cy={cy} r={4} fill={chartColors.planned} /> {payload.planAcc > 0 && <text x={cx} y={cy - 12} fill={chartColors.planned} fontSize={9} fontWeight="bold" textAnchor="middle">{payload.planAcc.toLocaleString(undefined, {maximumFractionDigits:0})}</text>} </g> ); }} />
                          <Line type="monotone" dataKey="actualAcc" stroke={chartColors.delay} strokeWidth={3} connectNulls={true} activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }} dot={(props) => { const { cx, cy, payload } = props; if (payload.actualAcc === null || payload.actualAcc === undefined || cx === undefined || cy === undefined) return null; return ( <g key={`dot-act-${payload.month}`}> <rect x={cx - 4} y={cy - 4} width={8} height={8} fill={chartColors.delay} rx={1} /> {payload.actualAcc > 0 && <text x={cx} y={cy + 18} fill={chartColors.delay} fontSize={9} fontWeight="bold" textAnchor="middle">{payload.actualAcc.toLocaleString(undefined, {maximumFractionDigits:0})}</text>} </g> ); }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* ═══ ส่วนที่ 2: Progress Bar ═══ */}
                  <div className="relative min-w-[700px] overflow-hidden bg-surface-alt border border-line rounded-input p-4 shadow-card shrink-0">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-ink-2 uppercase tracking-wider">เปรียบเทียบ ณ เดือนปัจจุบัน</span>
                      <span className="text-[11px] font-semibold text-ink-2">ระยะโครงการ: <span className="text-ink font-mono font-semibold">{routeTotal.toLocaleString()} ม.</span></span>
                    </div>

                    <div className="flex flex-col gap-3 relative z-20">
                      {/* แผนงานสะสม ณ เดือนปัจจุบัน */}
                      <div className="flex items-center h-[38px]">
                        <span className="w-32 text-xs font-semibold text-ink shrink-0">แผนงานสะสม</span>
                        <div className="flex-1 h-full rounded-input border border-line relative overflow-hidden bg-surface-page">
                          {totalPlanDistance > 0 ? (
                            <>
                              <div className="h-full bg-navy-dark rounded-input flex items-center justify-end px-3 relative z-10 shadow-card transition-all duration-700" style={{ width: `${Math.max((totalPlanDistance / routeTotal) * 100, 5)}%` }}>
                                <span className="text-white text-xs font-mono font-semibold drop-shadow-md whitespace-nowrap">{totalPlanDistance.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                              </div>
                              <div className="absolute top-0 right-2 h-full flex items-center z-0"><span className="text-[9px] font-semibold text-ink-3">{routeTotal.toLocaleString()}</span></div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><span className="text-ink-2 text-xs font-semibold">ยังไม่ตั้งค่าแผน — กดปุ่ม ⚙️ เพื่อตั้งค่า</span></div>
                          )}
                        </div>
                      </div>

                      {/* ผลงานจริง */}
                      <div className="flex items-center h-[38px] relative">
                        <span className="w-32 text-xs font-semibold text-ink shrink-0">ผลงานจริง</span>
                        <div className="flex-1 h-full rounded-input border border-line relative overflow-hidden bg-surface-page">
                          {(() => {
                            const planPercent = totalPlanDistance > 0 ? (totalPlanDistance / routeTotal) * 100 : 0;
                            const actualPercent = (totalActualDistance / routeTotal) * 100;
                            const gapWidth = planPercent - actualPercent;

                            return (
                              <>
                                {totalActualDistance > 0 && (
                                  <div className="absolute top-0 left-0 h-full bg-sgreen-dark flex items-center justify-center z-10 shadow-card transition-all duration-700" style={{ width: `${Math.max(actualPercent, 3)}%`, borderRight: totalPlanDistance > 0 ? '2px solid #0C2C65' : 'none' }}>
                                    <span className="text-white text-[10px] font-mono font-semibold whitespace-nowrap px-1 drop-shadow-md">{totalActualDistance.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
                                  </div>
                                )}
                                {totalPlanDistance > 0 && gapWidth > 0 && (
                                  <div className="absolute top-0 h-full flex items-center justify-center z-5" style={{ left: `${actualPercent}%`, width: `${gapWidth}%`, backgroundImage: "repeating-linear-gradient(45deg, #fecaca 0px, #fecaca 6px, #fff 6px, #fff 12px)" }}>
                                    <span className="text-code-d text-[9px] font-mono font-semibold bg-white/90 px-1.5 py-0.5 rounded-badge shadow-card whitespace-nowrap border border-code-d/30 z-10 relative">
                                      -{(totalPlanDistance - totalActualDistance).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                    </span>
                                  </div>
                                )}
                                <div className="absolute top-0 right-2 h-full flex items-center z-0"><span className="text-[9px] font-semibold text-ink-3">{routeTotal.toLocaleString()}</span></div>
                                {totalActualDistance === 0 && <div className="w-full h-full flex items-center justify-center z-20 relative"><span className="text-ink-3 text-[10px] font-semibold">ยังไม่มีผลงาน</span></div>}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ═══ ส่วนที่ 3: Route Progress ═══ */}
                  <div className="relative min-w-[700px] overflow-hidden bg-surface border border-line rounded-input p-4 shadow-card shrink-0">
                    <style>{` .track-pattern { background-image: repeating-linear-gradient(90deg, transparent, transparent 15px, #94a3b8 15px, #94a3b8 18px); } .track-pattern-vertical { background-image: repeating-linear-gradient(0deg, transparent, transparent 10px, #94a3b8 10px, #94a3b8 13px); } .track-pattern-light-vertical { background-image: repeating-linear-gradient(0deg, transparent, transparent 10px, #cbd5e1 10px, #cbd5e1 13px); } `}</style>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-ink-2 text-[11px] font-semibold tracking-[0.2em] uppercase">Route Progress</span>
                    </div>
                    <div className="relative mx-4 sm:mx-8" style={{ height: '85px' }}>
                      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[18px] bg-surface-alt border-y-[3px] border-line track-pattern shadow-card z-0"></div>
                      <div className="absolute top-1/2 -translate-y-1/2 left-0 h-[32px] rounded-l-full z-1 transition-all duration-1000" style={{ width: `${Math.min((totalActualDistance / routeTotal) * 100, 100)}%`, backgroundColor: `${chartColors.delay}33`, border: `1px solid ${chartColors.delay}66` }}></div>
                      {(() => {
                        const is3Seg = routeStationPlacements.find(s => s.id === "IS3");
                        if (!is3Seg) return null;
                        return (
                          <div className="absolute z-20" style={{ left: `${is3Seg.xPercent}%`, top: 'calc(50% - 16px)', transform: 'translateX(-50%)', width: 2, height: 32, borderLeft: "2.5px dashed #f59e0b" }} />
                        );
                      })()}
                      {(() => {
                        const is3Seg = routeStationPlacements.find(s => s.id === "IS3");
                        if (!is3Seg) return null;
                        return (
                          <div className="absolute z-0" style={{ left: `calc(${is3Seg.xPercent}%)`, top: 'calc(50% - 30px)', width: 2, height: '25px', borderLeft: '1.5px dashed #f59e0b' }} />
                        );
                      })()}
                      {routeStationPlacements.map((seg, i) => {
                        if (i === 0) return null;
                        const prevSeg = routeStationPlacements[i - 1];
                        return (
                          <React.Fragment key={`route-modal-${seg.id}`}>
                            <div className="absolute -top-6 -translate-x-1/2 bg-surface border-2 border-navy rounded-full px-2 py-0.5 text-[9px] font-semibold font-mono text-ink shadow-card whitespace-nowrap z-10" style={{ left: `${(prevSeg.xPercent + seg.xPercent) / 2}%` }}>{((seg.distance - routeSegments[i - 1].distance)).toLocaleString(undefined, { minimumFractionDigits: 3 })} m</div>
                            <div className="absolute flex flex-col items-center justify-center z-20" style={seg.id === "IS3" ? { left: `${seg.xPercent}%`, top: `calc(50% - 38px)`, transform: 'translateX(-50%) translateY(-50%)' } : { left: `${seg.xPercent}%`, top: `50%`, transform: 'translateX(-50%) translateY(-50%)' }}>
                              {seg.id === "IS3" ? (
                                <div className="relative flex flex-col items-center">
                                  <span className="absolute whitespace-nowrap text-[9px] font-semibold text-code-b bg-code-b/10 border border-code-b/30 px-1.5 py-0.5 rounded-badge shadow-card tracking-wide" style={{ top: -24 }}>
                                    D-wall
                                  </span>
                                  <div className="relative bg-navy-dark border-[1.5px] border-navy rounded-input p-1 shadow-card flex items-center justify-center z-10 w-11 h-11 pointer-events-none">
                                    <div className="bg-surface w-full h-full flex items-center justify-center rounded-badge">
                                      <div className="w-6 h-6 rounded-full text-white flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: chartColors.planned }}>{seg.label.replace("IS", "")}</div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="relative w-9 h-9 rounded-full border-[4px] border-white shadow-hover text-white flex items-center justify-center text-[10px] font-semibold z-10 tracking-tighter" style={{ backgroundColor: chartColors.planned }}>{seg.label.replace("IS", "")}</div>
                              )}
                              <span className="absolute text-[10px] font-semibold text-ink whitespace-nowrap" style={{ top: seg.id === "IS3" ? 66 : 44 }}>{seg.label}</span>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 flex flex-col items-center justify-center z-20">
                        <div className="relative w-9 h-9 rounded-full border-[4px] border-white shadow-hover text-white flex items-center justify-center text-[10px] font-semibold z-10 tracking-tighter" style={{ backgroundColor: chartColors.planned }}>{routeSegments[0].label.replace("IS", "")}</div>
                        <span className="absolute top-11 text-[10px] font-semibold text-ink whitespace-nowrap">{routeSegments[0].label}</span>
                      </div>
                      <div className="absolute top-1/2 -translate-y-1/2 z-30 transition-all duration-[1500ms] ease-out" style={{ left: `${Math.min((totalActualDistance / routeTotal) * 100, 100)}%`, transform: 'translateX(-50%) translateY(-50%)' }}>
                        <div className="relative drop-shadow-xl group cursor-pointer">
                          <span className="absolute -inset-2 rounded-full opacity-40 animate-ping" style={{ backgroundColor: chartColors.delay }}></span>
                          <div className="w-8 h-8 bg-navy border-[3px] border-white flex items-center justify-center rounded-badge rotate-90 relative z-10 shadow-hover overflow-hidden">
                            <div className="w-full h-[60%] bg-navy-dark absolute bottom-0 left-0"></div><div className="w-full h-[2px] bg-white opacity-50 absolute top-[40%] left-0"></div><span className="text-[8px] text-white font-semibold z-10 -rotate-90">{projectInfo?.tbmNo || "TBM"}</span>
                          </div>
                          <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-semibold text-ink bg-surface/90 backdrop-blur-sm px-1.5 py-0.5 rounded-badge shadow-card border border-line z-50">Progress : {((totalActualDistance / routeTotal) * 100).toFixed(2)} %</div>
                          <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-navy-dark text-white text-[10px] px-3 py-1.5 rounded-input font-semibold whitespace-nowrap shadow-modal opacity-0 group-hover:opacity-100 transition-opacity z-40 pointer-events-none">{projectInfo?.tbmNo || "TBM"}: {totalActualDistance.toLocaleString()} m ({(totalActualDistance / routeTotal * 100).toFixed(1)}%)</div>
                        </div>
                      </div>
                    </div>
                    <div className="h-8"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteScheduleView;
